import {
    Box,
    Button,
    Divider,
    Flex,
    Heading,
    Icon,
    Spinner,
    Text,
    Textarea,
    VStack,
    useColorModeValue,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { FaArrowRight, FaCheckCircle, FaRobot } from "react-icons/fa/index.js";

interface Interaction {
    id: string;
    question: string;
    answer: string;
}

const API_ENDPOINT = '/.netlify/functions/chat';
const INITIAL_QUESTION = "What's the biggest operational bottleneck slowing down your business today?";
const MAX_TURNS = 3;

export const AIDiscoveryEngine: React.FC = () => {
    const [interactions, setInteractions] = useState<Interaction[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState(INITIAL_QUESTION);
    const [inputValue, setInputValue] = useState("");
    const [isComplete, setIsComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Theme values
    const bgContainer = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const questionColor = useColorModeValue("blue.800", "blue.100");
    const historyBg = useColorModeValue("gray.50", "gray.900");
    const historyTextColor = useColorModeValue("gray.600", "gray.400");
    const historyAnswerColor = useColorModeValue("gray.800", "gray.200");
    const inputBg = useColorModeValue("white", "gray.700");

    const historyEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (interactions.length > 0) {
            historyEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [interactions]);

    const handleNextPhase = async () => {
        if (!inputValue.trim() || isLoading) return;

        const currentAnswer = inputValue.trim();
        const newInteraction: Interaction = {
            id: Date.now().toString(),
            question: currentQuestion,
            answer: currentAnswer
        };

        const updatedInteractions = [...interactions, newInteraction];
        setInteractions(updatedInteractions);
        setInputValue("");

        if (updatedInteractions.length >= MAX_TURNS) {
            setIsComplete(true);
            return;
        }

        setIsLoading(true);

        try {
            // Build Gemini-format history
            const geminiHistory = [];
            for (const interaction of updatedInteractions) {
                geminiHistory.push({ role: 'model', parts: [{ text: interaction.question }] });
                geminiHistory.push({ role: 'user', parts: [{ text: interaction.answer }] });
            }

            const res = await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    messages: geminiHistory
                }),
            });

            const data = await res.json();
            const reply = data.reply || "What other manual processes take up too much of your time?";
            
            setCurrentQuestion(reply);
        } catch (error) {
            console.error("AI Discovery Engine Error:", error);
            setCurrentQuestion("What other manual processes take up too much of your time?");
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleNextPhase();
        }
    };

    return (
        <Box
            w="full"
            bg={bgContainer}
            borderRadius="2xl"
            boxShadow="sm"
            border="1px solid"
            borderColor={borderColor}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            p={5}
        >
            {/* Engine Header / Tagline */}
            <Flex align="center" mb={4}>
                <Icon as={FaRobot} color="blue.500" w={5} h={5} mr={3} />
                <Text fontWeight="bold" fontSize="sm" color="blue.500" textTransform="uppercase" letterSpacing="wider">
                    Dynamic AI Discovery
                </Text>
            </Flex>

            <AnimatePresence mode="wait">
                {!isComplete ? (
                    <motion.div
                        key="active-discovery"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                    >
                        {/* Current Focus Question */}
                        <Box flex="1" display="flex" alignItems="center" justifyContent="center" mb={4} minH="60px">
                            {isLoading ? (
                                <VStack spacing={3}>
                                    <Spinner color="blue.500" />
                                    <Text fontSize="sm" color="gray.500">AI is analyzing your business...</Text>
                                </VStack>
                            ) : (
                                <Heading
                                    size="md"
                                    color={questionColor}
                                    lineHeight="1.4"
                                    textAlign="center"
                                    as={motion.h3}
                                    key={currentQuestion}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 } as any}
                                >
                                    {currentQuestion}
                                </Heading>
                            )}
                        </Box>

                        {/* Input Action Area */}
                        <VStack spacing={4} align="stretch" w="full">
                            <Textarea
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Type your insights here... (Press Enter to submit)"
                                size="sm"
                                resize="none"
                                rows={2}
                                bg={inputBg}
                                borderRadius="xl"
                                focusBorderColor="blue.500"
                                fontSize="sm"
                                isDisabled={isLoading}
                            />
                            <Button
                                rightIcon={<FaArrowRight />}
                                colorScheme="blue"
                                size="md"
                                w="full"
                                onClick={handleNextPhase}
                                isDisabled={!inputValue.trim() || isLoading}
                                isLoading={isLoading}
                                borderRadius="xl"
                                _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                                transition="all 0.2s"
                            >
                                Submit & Continue
                            </Button>
                        </VStack>
                    </motion.div>
                ) : (
                    <motion.div
                        key="completion"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}
                    >
                        <Icon as={FaCheckCircle} color="green.500" w={12} h={12} mb={4} />
                        <Heading size="sm" mb={2} color={questionColor}>Discovery Complete</Heading>
                        <Text fontSize="md" color={historyTextColor} mb={6}>
                            Our engineering team has received your operational footprint. We will reach out with a custom automation blueprint for your business.
                        </Text>
                        <Button
                            colorScheme="green"
                            size="md"
                            w="full"
                            borderRadius="xl"
                            onClick={() => window.location.href = '/contact-us'}
                        >
                            Schedule Executive Review
                        </Button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Context / History Container */}
            {interactions.length > 0 && (
                <Box mt={4}>
                    <Divider mb={3} />
                    <Box
                        bg={historyBg}
                        p={3}
                        borderRadius="xl"
                        maxH="120px"
                        overflowY="auto"
                        border="1px solid"
                        borderColor={borderColor}
                        as={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Text fontSize="xs" fontWeight="bold" color="blue.500" mb={2} textTransform="uppercase">
                            Discovery Context
                        </Text>
                        <VStack spacing={3} align="stretch">
                            {interactions.map((interaction, idx) => (
                                <Box key={interaction.id}>
                                    <Text fontSize="xs" color={historyTextColor} fontWeight="semibold" mb={1}>
                                        {idx + 1}. {interaction.question}
                                    </Text>
                                    <Text fontSize="sm" color={historyAnswerColor} pl={2} borderLeft="2px solid" borderColor="blue.500">
                                        {interaction.answer}
                                    </Text>
                                </Box>
                            ))}
                            <div ref={historyEndRef} />
                        </VStack>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
